import React, { useRef, useState } from 'react';
import { Card, Typography, Button, makeStyles } from '@material-ui/core';
import { toPng, toJpeg } from 'html-to-image';
import PropTypes from 'prop-types';
import { useRecordContext, TextInput, SimpleForm} from 'react-admin';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    marginBottom: theme.spacing(2),
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[3],
    }
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  qrContainer: {
    padding: 0,
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  button: {
    borderRadius: '8px',
    transition: 'all 0.2s cubic-bezier(0.42, 0, 0.58, 1)',
    '&:hover': {
      transform: 'translateY(-1px)',
    }
  },
  downloadButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    }
  },
  info: {
    marginTop: theme.spacing(2),
    marginBottom: 0,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    textAlign: "center"
  }
}));

const QrCodePreview = () => {
  const classes = useStyles();
  const qrRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const record = useRecordContext();

  const handleDownload = async (format) => {
    if (!qrRef.current) return;
    
    setDownloading(true);
    
    try {
      const fileName = `qrcode-${record.hash}`;
      const size = document.getElementById('qr_size').value;
      let dataUrl;
      const options = {canvasWidth: size, canvasHeight: size};

      switch (format) {
        case 'jpg' :
          dataUrl = await toJpeg(qrRef.current, options);
        break;
        case 'png' :
          dataUrl = await toPng(qrRef.current, options);
        break;
        default :
          dataUrl = record.image_url;
          format = 'svg';
      }

      const link = document.createElement('a');
      link.download = `${fileName}.${format}`;
      link.href = dataUrl;
      if (format === 'svg') {
        link.target = "_blank";
      }
      link.click();

    } catch (error) {
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };
  
  if (!record || !record.url) {
    return (
        <Card className={classes.root}>
          <Typography variant="h6" className={classes.title}>
            QR Code Preview
          </Typography>
        </Card>
    );
  }

  return (
      <Card className={classes.root}>
        <Typography variant="h6" className={classes.title}>
          QR Code Preview
        </Typography>
        <div className={classes.qrContainer} ref={qrRef}>
          <img src={record.image_url} width="200" height="200" alt="Qr Code"/>
        </div>
        <div className={classes.buttonGroup}>
          <Button
              variant="contained"
              className={`${classes.button} ${classes.downloadButton}`}
              onClick={() => handleDownload('svg')}
              disabled={downloading}
          >
            Download Source SVG
          </Button>
        </div>
        <Typography variant="body2" className={classes.info}>
          <SimpleForm toolbar={null}>
            <Typography variant="button" >
              Download with the size below in PNG or JPG
            </Typography>
            <TextInput id="qr_size" name="qr_size" defaultValue="600"/>
          </SimpleForm>
        </Typography>
        <div className={classes.buttonGroup}>
          <Button
              variant="contained"
              className={`${classes.button} ${classes.downloadButton}`}
              onClick={() => handleDownload('png')}
              disabled={downloading}
          >
            Download PNG
          </Button>
          <Button
              variant="contained"
              className={`${classes.button} ${classes.downloadButton}`}
              onClick={() => handleDownload('jpg')}
              disabled={downloading}
          >
            Download JPG
          </Button>
        </div>
      </Card>
  );
};

QrCodePreview.propTypes = {
  record: PropTypes.object
};

export default QrCodePreview;